import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import api from "@/services/api";

// Values come from .env (VUE_APP_FIREBASE_*) — currently the TEST project
// (nanosoft-hr-push-test). public/firebase-messaging-sw.js is a static file
// that webpack does not process, so it cannot read these and keeps its own
// hardcoded copy — update both places together. See docs/Notifications.md.
const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY;

// Set to whichever token value was last successfully POSTed to the backend —
// lets us skip re-registering a device that hasn't actually changed.
export const REGISTERED_TOKEN_KEY = "pushRegisteredDeviceToken";

// Set to the subscriberId that REGISTERED_TOKEN_KEY was registered under.
// The FCM token is tied to the browser/device, not to whichever HR user is
// logged in — so if a different user logs in on the same browser, Firebase
// hands back the SAME token. Without tracking which subscriber it was last
// sent for, the dedup check below would see "this token is already
// registered" and silently skip the network call, leaving the new user's
// device never actually registered with Novu (and still receiving the
// previous user's push notifications).
export const REGISTERED_SUBSCRIBER_KEY = "pushRegisteredSubscriberId";

// Set to the Novu applicationIdentifier (VUE_APP_NOVU_APP_ID) that
// REGISTERED_TOKEN_KEY was registered under. The backend registers the token
// into whichever Novu environment it currently targets, so a token registered
// under one environment (e.g. Development) doesn't exist in another (e.g.
// Production after the 2026-10-01 cutover). Recording which environment the
// registration was made under makes any environment change force a fresh
// registration automatically, without requiring users to log out.
export const REGISTERED_NOVU_APP_KEY = "pushRegisteredNovuAppId";
export const CURRENT_NOVU_APP_ID = process.env.VUE_APP_NOVU_APP_ID || "";

// Set to the subscriberId of whoever picked "رفض" in the banner's dialog —
// a permanent "no" distinct from just not having decided yet. Stores the
// subscriber, not just a flag, for the same reason as REGISTERED_SUBSCRIBER_KEY
// above: this is browser-wide localStorage, so a plain "1" would make one
// user's decline silently suppress the banner for every other user who
// later logs into HR on the same browser/device.
export const DISMISS_KEY = "pushBannerDismissed";

let messagingInstance = null;

function withTimeout(promise, ms, timeoutMessage) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(timeoutMessage)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance;
  if (!firebaseConfig.apiKey || !VAPID_KEY) {
    throw new Error(
      "إعدادات Firebase غير موجودة في .env، يرجى مراجعة docs/Notifications.md"
    );
  }
  const supported = await isSupported();
  if (!supported) {
    throw new Error("هذا المتصفح لا يدعم خاصية Web Push");
  }
  const app = initializeApp(firebaseConfig);
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

export async function enablePushNotifications() {
  // Ask permission FIRST, on the freshest possible user click — some
  // browsers show a subtle "quiet UI" icon near the address bar (instead of
  // a blocking prompt) for sites that were asked before, and any async work
  // done ahead of this call risks the click's "user activation" expiring.
  const permission = await withTimeout(
    Notification.requestPermission(),
    20000,
    "لم نتمكن من الحصول على رد على إذن الإشعارات خلال 20 ثانية، يرجى التأكد من عدم تفويت أيقونة صغيرة بجانب شريط العنوان (يعرضها متصفح Chromium أحيانًا بدلاً من نافذة واضحة)"
  );
  if (permission !== "granted") {
    throw new Error("رفض المستخدم إذن الإشعارات");
  }

  const registration = await withTimeout(
    navigator.serviceWorker.register("/firebase-messaging-sw.js"),
    10000,
    "استغرق تسجيل الـ Service Worker وقتًا طويلاً جدًا، يرجى المحاولة مرة أخرى أو التأكد من الاتصال بالإنترنت"
  );

  const messaging = await getMessagingInstance();
  const token = await withTimeout(
    getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    }),
    15000,
    "لم نتمكن من الحصول على رمز من خوادم Google خلال 15 ثانية، غالبًا يوجد VPN أو جدار حماية للشبكة يمنع الاتصال بخوادم Google الخاصة بالإشعارات (fcm.googleapis.com)، يرجى إيقاف تشغيل الـ VPN أو التأكد من إعدادات الشبكة"
  );

  if (!token) {
    throw new Error("تعذّر الحصول على الرمز، يرجى المحاولة مرة أخرى");
  }

  return token;
}

// POST /api/notifications/registerDevice — the one thing the backend needs
// per device/browser. Called once right after we have a token, and again
// only if the token ever changes (see registerDeviceTokenIfNeeded below) —
// not on every app load, per the backend's contract.
export async function registerDeviceToken(token, subscriberId) {
  if (!subscriberId) {
    throw new Error("subscriberId غير موجود، يجب تسجيل الدخول أولاً");
  }
  await api.post("/api/notifications/registerDevice", {
    subscriberId: String(subscriberId),
    deviceToken: token,
    platform: "web",
  });
  localStorage.setItem(REGISTERED_TOKEN_KEY, token);
  localStorage.setItem(REGISTERED_SUBSCRIBER_KEY, String(subscriberId));
  localStorage.setItem(REGISTERED_NOVU_APP_KEY, CURRENT_NOVU_APP_ID);
}

// Skips the network call only when the token, the subscriber AND the Novu
// environment all match what was last registered — matching the token alone
// isn't enough, since the same browser/device can be shared by different
// logged-in users (see the note on REGISTERED_SUBSCRIBER_KEY above), and a
// registration made under another Novu environment doesn't carry over (see
// REGISTERED_NOVU_APP_KEY).
export async function registerDeviceTokenIfNeeded(token, subscriberId) {
  const alreadyRegistered =
    localStorage.getItem(REGISTERED_TOKEN_KEY) === token &&
    localStorage.getItem(REGISTERED_SUBSCRIBER_KEY) === String(subscriberId) &&
    localStorage.getItem(REGISTERED_NOVU_APP_KEY) === CURRENT_NOVU_APP_ID;
  if (alreadyRegistered) return false;
  await registerDeviceToken(token, subscriberId);
  return true;
}

// Foreground = the HR tab is open and focused when the push arrives, so
// Firebase hands the message to onMessage() here instead of the service
// worker, and nothing shows it unless the page does. This path is kept
// independent of enablePushNotifications()/getToken() on purpose: it only
// needs the SW that receives the push to be active, not a fresh token — so a
// slow or network-blocked getToken() on this page load can't stop foreground
// notifications from showing.
export async function listenForegroundMessages(callback) {
  const messaging = await getMessagingInstance();
  // onMessage() only ever fires if firebase-messaging-sw.js is registered and
  // active. getToken() normally does that, but this can run before/without it —
  // register here too. Idempotent: resolves to the existing registration when
  // one is already active, with no re-download.
  try {
    await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  } catch (error) {
    // Best effort — a previous session has almost certainly registered it,
    // and onMessage() below is what actually matters here.
  }
  onMessage(messaging, callback);
}
